import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { Fragment, useEffect, useState, type ReactElement, type ReactNode } from "react";
import { ContentHeader, Field, Modal, QueryStatus, StatusBadge, useToast } from "@cx/ui";
import type {
  SalesOrderStatusHistoryItem,
  SalesOrderStatusRef,
  ServiceOrderFinishResult,
  ServiceOrderFormOptions,
  ServiceOrderInvoicesResult,
  ServiceOrderPdfLink,
  ServiceOrderView,
} from "@cx/contracts";
import {
  SERVICE_ORDER_FINISHED,
  formatBRL,
  formatDateBR,
  formatDocument,
  formatQty,
  todayInSaoPaulo,
} from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { INVOICE_STATUS_LABEL, INVOICE_STATUS_TONE } from "../invoices/labels";
import {
  FINANCIAL_EFFECT_LABELS,
  STOCK_EFFECT_LABELS,
  historyLabel,
  historySteps,
} from "../sales-orders/labels";
import { NFSE_STATUS_LABEL, NFSE_STATUS_TONE } from "../services/labels";
import { hoursLabel, invoicesSummary } from "./labels";

const dateTimeBR = (iso: string) =>
  new Date(iso).toLocaleString("pt-BR", {
    timeZone: "America/Sao_Paulo",
    dateStyle: "short",
    timeStyle: "short",
  });

type NotesDialog = { mode: "finish" | "invoices" } | null;

/**
 * OS gravada: equipamento, serviços e peças, totais, situação com histórico; "Finalizar" (baixa das peças, contas a
 * receber e, se pedido, NFS-e dos serviços e NF-e das peças), "Emitir notas" da OS finalizada e o PDF da OS ou do
 * comprovante de entrada.
 */
export function ServiceOrderViewPage(): ReactElement {
  const { id } = useParams({ from: "/servicos/ordens-de-servico/$id" });
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const qc = useQueryClient();
  const toast = useToast();
  const canUpdate = can(perms, "service_orders", "update");
  const order = useQuery({
    queryKey: ["service-orders", companyId, "view", id],
    refetchOnMount: "always",
    queryFn: () => api<ServiceOrderView>(`/service-orders/${id}`),
  });
  const history = useQuery({
    queryKey: ["service-orders", companyId, "history", id],
    refetchOnMount: "always",
    queryFn: () => api<{ data: SalesOrderStatusHistoryItem[] }>(`/service-orders/${id}/status-history`),
  });
  // O código da situação (para achar a "Finalizada" de sistema) vem das opções; só quem altera precisa.
  const options = useQuery({
    queryKey: ["service-orders", companyId, "form-options"],
    enabled: canUpdate,
    queryFn: () => api<ServiceOrderFormOptions>("/service-orders/form-options"),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["service-orders", companyId] });
  const onError = (e: Error) => {
    toast("danger", errorMessage(e));
    refresh();
  };
  const status = useMutation({
    mutationFn: (to: SalesOrderStatusRef) =>
      api(`/service-orders/${id}/status`, {
        method: "POST",
        headers: { "Idempotency-Key": crypto.randomUUID() },
        body: JSON.stringify({ toStatusId: to.id, expectedFromStatusId: order.data!.status.id }),
      }),
    onSuccess: (_d, to) => {
      toast("success", `Situação alterada para ${to.label}`);
      refresh();
      qc.invalidateQueries({ queryKey: ["stock", companyId] });
    },
    onError,
  });
  const pdf = useMutation({
    mutationFn: (kind: "os" | "receipt") =>
      api<ServiceOrderPdfLink>(`/service-orders/${id}/pdf${kind === "receipt" ? "?kind=receipt" : ""}`),
    onSuccess: (r) => window.location.assign(r.url),
    onError,
  });
  const [dialog, setDialog] = useState<NotesDialog>(null);

  const o = order.data;
  const finishedId = options.data?.statuses.find((s) => s.code === SERVICE_ORDER_FINISHED)?.id ?? null;
  const canFinish = !!o && canUpdate && !!finishedId && o.nextStatuses.some((s) => s.id === finishedId);
  const canInvoice = can(perms, "invoices", "create");
  const notesOpen = (n: { status: string } | null) => !n || n.status === "draft" || n.status === "rejected";
  const showEmit =
    !!o &&
    canUpdate &&
    canInvoice &&
    o.invoiceable &&
    (notesOpen(o.invoices.nfse) || notesOpen(o.invoices.nfe));
  const busy = status.isPending;

  return (
    <>
      <ContentHeader
        title={o ? `Ordem de serviço nº ${o.number}` : "Ordem de serviço"}
        breadcrumb={["Serviços", "Ordens de serviço", o ? String(o.number) : ""]}
        actions={
          o && (
            <>
              <Link to="/servicos/ordens-de-servico" className="btn btn-outline-secondary">
                Voltar
              </Link>
              <div className="btn-group" role="group" aria-label="Imprimir">
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  disabled={pdf.isPending}
                  onClick={() => pdf.mutate("os")}
                >
                  {pdf.isPending ? "Gerando PDF…" : "Imprimir OS"}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  disabled={pdf.isPending}
                  onClick={() => pdf.mutate("receipt")}
                >
                  Comprovante de entrada
                </button>
              </div>
              {canUpdate && !o.lockedReason && (
                <Link
                  to="/servicos/ordens-de-servico/$id/editar"
                  params={{ id: o.id }}
                  className="btn btn-outline-primary"
                >
                  Editar
                </Link>
              )}
              {showEmit && (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setDialog({ mode: "invoices" })}
                >
                  Emitir notas
                </button>
              )}
              {canFinish && (
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={busy}
                  onClick={() => setDialog({ mode: "finish" })}
                >
                  Finalizar
                </button>
              )}
            </>
          )
        }
      />
      <QueryStatus loading={order.isLoading} error={order.error} />
      {o && (
        <div className="row">
          <div className="col-xl-9">
            <div className="box card p-3 mb-3">
              <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <span>Situação:</span>
                <StatusBadge tone={o.status.color}>{o.status.label}</StatusBadge>
                {canUpdate && o.nextStatuses.length > 0 && (
                  <div className="d-flex flex-wrap gap-1 ms-md-3" role="group" aria-label="Alterar situação">
                    {o.nextStatuses
                      .filter((s) => s.id !== finishedId)
                      .map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          disabled={busy}
                          onClick={() => status.mutate(s)}
                        >
                          Marcar como {s.label}
                        </button>
                      ))}
                  </div>
                )}
              </div>
              <dl className="row mb-0">
                <Info label="Cliente">
                  {o.contact.name}
                  {o.contact.document && (
                    <span className="text-muted ms-2">{formatDocument(o.contact.document)}</span>
                  )}
                </Info>
                <Info label="Equipamento">{o.equipment}</Info>
                <Info label="Nº de série">{o.serialNumber}</Info>
                <Info label="Problema relatado">{o.problem}</Info>
                <Info label="Técnico">{o.technician}</Info>
                <Info label="Vendedor">{o.seller?.name}</Info>
                {o.store && <Info label="Loja">{o.store.name}</Info>}
                <Info label="Entrada">
                  {formatDateBR(o.entryDate)}
                  {o.startTime && ` ${o.startTime}`}
                </Info>
                <Info label="Previsão">{o.expectedDate && formatDateBR(o.expectedDate)}</Info>
                <Info label="Término">
                  {o.finishDate && `${formatDateBR(o.finishDate)}${o.endTime ? ` ${o.endTime}` : ""}`}
                </Info>
                {o.exitDate && <Info label="Saída">{formatDateBR(o.exitDate)}</Info>}
                {(o.quoteMode || o.quoted) && (
                  <Info label="Orçamento">{o.quoted ? "Orçado" : "A orçar"}</Info>
                )}
                {o.invoices.nfse && can(perms, "invoices", "read") && (
                  <Info label="Nota de serviço">
                    <Link to="/servicos/notas-de-servico/$id" params={{ id: o.invoices.nfse.id }}>
                      {o.invoices.nfse.number ? `NFS-e nº ${o.invoices.nfse.number}` : "NFS-e"}
                    </Link>{" "}
                    <StatusBadge tone={NFSE_STATUS_TONE[o.invoices.nfse.status]}>
                      {NFSE_STATUS_LABEL[o.invoices.nfse.status]}
                    </StatusBadge>
                  </Info>
                )}
                {o.invoices.nfe && can(perms, "invoices", "read") && (
                  <Info label="Nota fiscal">
                    <Link to="/vendas/notas-fiscais/$id" params={{ id: o.invoices.nfe.id }}>
                      {o.invoices.nfe.number === null
                        ? "NF-e (rascunho)"
                        : `NF-e nº ${o.invoices.nfe.number}`}
                    </Link>{" "}
                    <StatusBadge tone={INVOICE_STATUS_TONE[o.invoices.nfe.status]}>
                      {INVOICE_STATUS_LABEL[o.invoices.nfe.status]}
                    </StatusBadge>
                  </Info>
                )}
              </dl>
            </div>
            <ItemsCard o={o} />
            {(o.serviceNotes ||
              o.technicianInfo ||
              o.warranty ||
              o.warrantyInfo ||
              o.receptionNotes ||
              o.internalNotes) && (
              <div className="box card p-3 mb-3">
                <h2 className="h6">Laudo, garantia e observações</h2>
                <dl className="row mb-0">
                  {o.receptionNotes && <Info label="Recebimento">{o.receptionNotes}</Info>}
                  {o.serviceNotes && <Info label="Observações do serviço">{o.serviceNotes}</Info>}
                  {o.technicianInfo && <Info label="Laudo técnico">{o.technicianInfo}</Info>}
                  {o.warranty && <Info label="Garantia">{o.warranty}</Info>}
                  {o.warrantyInfo && <Info label="Condições da garantia">{o.warrantyInfo}</Info>}
                  {o.internalNotes && <Info label="Observações internas">{o.internalNotes}</Info>}
                </dl>
              </div>
            )}
            <div className="box card p-3 mb-3">
              <h2 className="h6">Histórico de situações</h2>
              <QueryStatus loading={history.isLoading} error={history.error} />
              {history.data && history.data.data.length === 0 && (
                <p className="text-muted small mb-0">Nenhuma mudança de situação ainda.</p>
              )}
              {history.data && history.data.data.length > 0 && (
                <div className="table-responsive">
                  <table className="table table-sm mb-0" aria-label="Histórico de situações">
                    <thead>
                      <tr>
                        <th scope="col">Quando</th>
                        <th scope="col">Mudança</th>
                        <th scope="col">Ações executadas</th>
                        <th scope="col">Usuário</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.data.data.map((h) => (
                        <tr key={h.id}>
                          <td className="text-nowrap">{dateTimeBR(h.createdAt)}</td>
                          <td>{historyLabel(h)}</td>
                          <td>{historySteps(h) || "—"}</td>
                          <td>{h.userName ?? "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
          <aside className="col-xl-3">
            <div className="box card p-3 mb-3">
              <h2 className="h6">Totais</h2>
              <dl className="row mb-0 small">
                {(
                  [
                    [`Serviços (${hoursLabel(o.hoursSumQ4)})`, o.servicesTotalCents],
                    ["Peças", o.partsTotalCents],
                    ["Frete", o.freightCents],
                    ["Desconto", -o.discountCents],
                  ] as const
                ).map(([label, v]) => (
                  <Fragment key={label}>
                    <dt className="col-7 fw-normal">{label}</dt>
                    <dd className="col-5 text-end mb-1">{formatBRL(v)}</dd>
                  </Fragment>
                ))}
                <dt className="col-7">Total da OS</dt>
                <dd className="col-5 text-end fw-bold mb-1" data-testid="os-total">
                  {formatBRL(o.totalCents)}
                </dd>
              </dl>
            </div>
            <div className="box card p-3 mb-3">
              <h2 className="h6">Pagamento</h2>
              <dl className="row mb-2 small">
                <dt className="col-6 fw-normal">Condição</dt>
                <dd className="col-6 text-end mb-1">{o.paymentTerm?.name ?? "À vista"}</dd>
                <dt className="col-6 fw-normal">Forma</dt>
                <dd className="col-6 text-end mb-1">{o.paymentMethod?.name ?? "—"}</dd>
                <dt className="col-6 fw-normal">Estoque</dt>
                <dd className="col-6 text-end mb-1" data-testid="os-stock">
                  {STOCK_EFFECT_LABELS[o.effects.stock]}
                </dd>
                <dt className="col-6 fw-normal">Contas</dt>
                <dd className="col-6 text-end mb-0" data-testid="os-financial">
                  {FINANCIAL_EFFECT_LABELS[o.effects.financial]}
                </dd>
              </dl>
              {o.titles.length > 0 && (
                <ul className="list-unstyled small mb-0" aria-label="Contas a receber da OS">
                  {o.titles.map((t, i) => (
                    <li key={t.id}>
                      {can(perms, "receivables", "read") ? (
                        <Link to="/financas/contas-a-receber/$id" params={{ id: t.id }}>
                          {i + 1}ª parcela
                        </Link>
                      ) : (
                        `${i + 1}ª parcela`
                      )}{" "}
                      — {formatDateBR(t.dueDate)} — {formatBRL(t.amountCents)}
                    </li>
                  ))}
                </ul>
              )}
              {o.lockedReason && <p className="text-muted small mt-2 mb-0">{o.lockedReason}</p>}
            </div>
          </aside>
        </div>
      )}
      {o && (
        <NotesDialogView
          state={dialog}
          order={o}
          finishedId={finishedId}
          canInvoice={canInvoice}
          taxOperations={options.data?.taxOperations ?? []}
          onClose={() => setDialog(null)}
          onDone={() => {
            setDialog(null);
            refresh();
            qc.invalidateQueries({ queryKey: ["stock", companyId] });
          }}
        />
      )}
    </>
  );
}

function ItemsCard({ o }: { o: ServiceOrderView }) {
  if (o.items.length === 0)
    return (
      <div className="box card p-3 mb-3">
        <p className="text-muted mb-0">Nenhum serviço ou peça.</p>
      </div>
    );
  return (
    <div className="box card mb-3">
      <div className="table-responsive">
        <table className="table align-middle mb-0" aria-label="Itens da OS">
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col">Tipo</th>
              <th scope="col">Descrição</th>
              <th scope="col">Un</th>
              <th scope="col" className="text-end">
                Qtd
              </th>
              <th scope="col" className="text-end">
                Preço un.
              </th>
              <th scope="col" className="text-end">
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {o.items.map((it) => (
              <tr key={it.position}>
                <td>{it.position}</td>
                <td>{it.kind === "service" ? "Serviço" : "Peça"}</td>
                <td>
                  {it.description}
                  {it.code && <span className="text-muted small ms-2">{it.code}</span>}
                  {it.toQuote && <span className="badge text-bg-light ms-2">Orçar</span>}
                  {it.done && <span className="badge text-bg-success ms-2">OK</span>}
                </td>
                <td>{it.unit ?? "—"}</td>
                <td className="text-end">{formatQty(it.qtyQ4, it.kind === "service" ? 2 : 0)}</td>
                <td className="text-end">{formatBRL(it.unitPriceCents)}</td>
                <td className="text-end">{formatBRL(it.totalCents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Finalizar (data de término e notas opcionais) ou "Emitir notas" da OS finalizada. NFS-e só com serviços; NF-e só
 * com peças. A nota que não sai (provedor desligado, cadastro incompleto) fica em rascunho e o motivo aparece no aviso.
 */
function NotesDialogView({
  state,
  order: o,
  finishedId,
  canInvoice,
  taxOperations,
  onClose,
  onDone,
}: {
  state: NotesDialog;
  order: ServiceOrderView;
  finishedId: string | null;
  canInvoice: boolean;
  taxOperations: { id: string; name: string; isDefault: boolean }[];
  onClose(): void;
  onDone(): void;
}) {
  const { api } = useSession();
  const toast = useToast();
  const hasServices = o.items.some((i) => i.kind === "service");
  const hasParts = o.items.some((i) => i.kind === "part");
  const open = !!state;
  const finishing = state?.mode === "finish";
  const [finishDate, setFinishDate] = useState("");
  const [nfse, setNfse] = useState(false);
  const [nfe, setNfe] = useState(false);
  const [operationId, setOperationId] = useState("");
  // Uma chave por abertura: o duplo clique não finaliza nem emite duas vezes.
  const [key, setKey] = useState(() => crypto.randomUUID());
  const nfseOk = hasServices && (!o.invoices.nfse || ["draft", "rejected"].includes(o.invoices.nfse.status));
  const nfeOk = hasParts && (!o.invoices.nfe || ["draft", "rejected"].includes(o.invoices.nfe.status));
  useEffect(() => {
    if (!open) return;
    setKey(crypto.randomUUID());
    setFinishDate(o.finishDate ?? todayInSaoPaulo());
    setNfse(canInvoice && nfseOk && !finishing);
    setNfe(canInvoice && nfeOk && !finishing);
    setOperationId("");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- só ao abrir
  }, [open]);
  const run = useMutation({
    mutationFn: () =>
      finishing
        ? api<ServiceOrderFinishResult>(`/service-orders/${o.id}/finish`, {
            method: "POST",
            headers: { "Idempotency-Key": key },
            body: JSON.stringify({
              expectedFromStatusId: o.status.id,
              finishDate: finishDate || null,
              nfse,
              nfe,
              taxOperationId: operationId || null,
            }),
          })
        : api<ServiceOrderInvoicesResult>(`/service-orders/${o.id}/invoices`, {
            method: "POST",
            headers: { "Idempotency-Key": key },
            body: JSON.stringify({ nfse, nfe, taxOperationId: operationId || null }),
          }),
    onSuccess: (r) => {
      const { ok, problems } = invoicesSummary(r);
      toast("success", [finishing ? "Ordem de serviço finalizada" : null, ...ok].filter(Boolean).join(" · "));
      for (const p of problems) toast("warning", `${p} A nota ficou em rascunho.`);
      onDone();
    },
    onError: (e) => {
      toast("danger", errorMessage(e));
      if (e instanceof ApiFailure && e.status !== 409) setKey(crypto.randomUUID());
    },
  });
  const nothing = !finishing && !nfse && !nfe;
  return (
    <Modal
      open={open}
      title={finishing ? `Finalizar OS nº ${o.number}` : `Emitir notas da OS nº ${o.number}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={run.isPending || nothing || (finishing && !finishedId)}
            onClick={() => run.mutate()}
          >
            {run.isPending ? "Enviando…" : finishing ? "Finalizar" : "Emitir"}
          </button>
        </>
      }
    >
      {finishing && (
        <>
          <p className="small text-muted">
            As peças saem do estoque e as contas a receber são lançadas ({formatBRL(o.totalCents)}
            {o.paymentTerm ? `, ${o.paymentTerm.name}` : ", à vista"}) a partir da data de término.
          </p>
          <div className="row">
            <Field label="Data de término" required className="col-md-6">
              <input
                type="date"
                value={finishDate}
                min={o.entryDate}
                onChange={(e) => setFinishDate(e.target.value)}
              />
            </Field>
          </div>
        </>
      )}
      {canInvoice ? (
        <fieldset className="mb-2">
          <legend className="form-label fs-6">{finishing ? "Emitir também" : "Notas"}</legend>
          <div className="form-check">
            <input
              id="os-nfse"
              type="checkbox"
              className="form-check-input"
              disabled={!nfseOk}
              checked={nfse}
              onChange={(e) => setNfse(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="os-nfse">
              NFS-e dos serviços{!hasServices && " (sem serviços)"}
              {hasServices && !nfseOk && " (já enviada)"}
            </label>
          </div>
          <div className="form-check">
            <input
              id="os-nfe"
              type="checkbox"
              className="form-check-input"
              disabled={!nfeOk}
              checked={nfe}
              onChange={(e) => setNfe(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="os-nfe">
              NF-e das peças{!hasParts && " (sem peças)"}
              {hasParts && !nfeOk && " (já enviada)"}
            </label>
          </div>
          {nfe && (
            <Field label="Natureza de operação da NF-e" className="col-12 mt-2">
              <select
                className="form-select"
                value={operationId}
                onChange={(e) => setOperationId(e.target.value)}
              >
                <option value="">Padrão da empresa</option>
                {taxOperations.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </Field>
          )}
        </fieldset>
      ) : (
        <p className="small text-muted mb-0">Sem permissão para emitir notas: a OS é finalizada sem notas.</p>
      )}
    </Modal>
  );
}

function Info({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="col-sm-4 col-lg-3 fw-normal text-muted">{label}</dt>
      <dd className="col-sm-8 col-lg-9" style={{ whiteSpace: "pre-wrap" }}>
        {children || "—"}
      </dd>
    </>
  );
}
